import { defineStore } from "pinia"
import type { IconType } from "~/types/base-icons"

export type PanelMenuItemIds =
	| "dashboard"
	| "manage-users"
	| "manage-payrolls"
	| "manage-reward-payrolls"
	| "manage-performance-reviews"
	| "manage-careers"
	| "manage-career-requests"
	| "manage-onboarding"
	| "manage-categories"
	| "manage-custom-pages"
	| "manage-attendances"
	| "manage-company-settings"
	| "manage-settings"
	| "manage-dev-tickets"
	| "manage-mails"
	| "manage-kitchen"
	| "manage-surveys"
	| "manage-benefit-funds"
	| "vira"
	| string

export type PanelMenuItem = {
	id: PanelMenuItemIds
	title: string
	description?: string
	icon?: IconType
	iconify?: string
	to: string
	color?: string
	yellow?: boolean
	roles?: (string | null)[]
	items?: PanelMenuItem[]
	company: boolean | null
	new?: boolean
}

export type CompanyPanelMenuItemIds =
	| "dashboard"
	| "manage-payrolls"
	| "manage-reward-payrolls"
	| "manage-performance-reviews"
	| "manage-settings"
	| string

export type CompanyPanelMenuItem = {
	id: CompanyPanelMenuItemIds
	title: string
	description?: string
	icon?: IconType
	iconify?: string
	to: string
	color?: string
	yellow?: boolean
	new?: number
	roles?: string[]
	company?: boolean | null
	items?: CompanyPanelMenuItem[]
}

export type AdminMenuItemIds =
	| "dashboard"
	| "manage-slides"
	| "manage-users"
	| "manage-categories"
	| "manage-rules"
	| "manage-contact-us"
	| "manage-about-us"
	| "manage-logs"
	| "manage-metas"
	| "manage-dev-tickets"
	| "manage-companies"
	| "show-website"
	| "article"
	| "help"

export type AdminMenuItem = {
	id: AdminMenuItemIds
	title: string
	icon: string
	to: string
	color: string
	yellow?: boolean
	roles: string[]
}

export const usePanelStore = defineStore("panel", () => {
	const { $i18n, $routeUrl, $stringUtil } = useNuxtApp()
	const userStore = useUserStore()
	const companyStore = useCompanyStore()
	const metaStore = useMetaStore()
	const postStore = usePostStore()

	const state = {
		adminMenuItems: ref<AdminMenuItem[]>([
			{
				id: "dashboard",
				title: $i18n.t("dashboard"),
				icon: "mdi:view-dashboard",
				to: $routeUrl.AdminDashboardUrl(),
				color: "#455a64",
				roles: ["admin", "support", "blogger", "developer"]
			},
			{
				id: "manage-users",
				title: $i18n.t("users"),
				icon: "mdi:account-supervisor",
				to: $routeUrl.ManageUsersUrl(),
				color: "#455a64",
				roles: ["admin", "support"]
			},
			{
				id: "manage-companies",
				title: $i18n.t("companies"),
				icon: "mdi:company",
				to: $routeUrl.ManageCompaniesUrl(),
				color: "#455a64",
				roles: ["admin", "support"]
			},
			{
				id: "manage-slides",
				title: $i18n.t("slides"),
				icon: "mdi:folder-image",
				to: $routeUrl.ManageSlidesUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-categories",
				title: $i18n.t("categories"),
				icon: "mdi:format-list-bulleted-square",
				to: $routeUrl.ManageCategoriesUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "article",
				title: $i18n.t("articles"),
				icon: "mdi:post",
				to: $routeUrl.ManagePostsUrl("article"),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "help",
				title: $i18n.t("helps"),
				icon: "solar:help-bold",
				to: $routeUrl.ManagePostsUrl("help"),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-rules",
				title: $i18n.t("rules"),
				icon: "mdi:gavel",
				to: $routeUrl.ManageRulesUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-about-us",
				title: $i18n.t("about_us"),
				icon: "mdi:card-account-phone",
				to: $routeUrl.ManageAboutUsUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-contact-us",
				title: $i18n.t("contact_us"),
				icon: "mdi:card-account-phone",
				to: $routeUrl.ManageContactUsUrl(),
				color: "#455a64",
				roles: ["admin", "blogger"]
			},
			{
				id: "manage-logs",
				title: $i18n.t("logs"),
				icon: "mdi:history",
				to: $routeUrl.ManageLogsUrl(),
				color: "#455a64",
				roles: ["admin"]
			},
			{
				id: "manage-metas",
				title: $i18n.t("metas"),
				icon: "mdi:application-cog",
				to: $routeUrl.ManageMetasUrl(),
				color: "#455a64",
				roles: ["admin"]
			},
			{
				id: "manage-dev-tickets",
				title: $i18n.t("technical_tickets"),
				icon: "mdi:lightbulb-question",
				to: $routeUrl.ManageDevTicketsUrl(),
				color: "#455a64",
				roles: ["admin", "support", "blogger", "developer"]
			},
			{
				id: "show-website",
				title: $i18n.t("show_website"),
				icon: "mdi:eye",
				to: $routeUrl.HomeUrl(),
				color: "#455a64",
				roles: ["admin", "support", "blogger", "developer"]
			}
		]),
		langs: ref<string[]>(["fa", "en"])
	}

	const panelMenuItems = computed<PanelMenuItem[]>(() => {
		return [
			// Overview
			{
				id: "dashboard",
				title: $i18n.t("dashboard"),
				iconify: "mdi:view-dashboard",
				to: $routeUrl.PanelDashboardUrl(),
				company: null,
				roles: [
					"admin",
					"support",
					"blogger",
					"developer",
					"user",
					userStore.user?.role_id ? "company-user" : null
				].filter((v) => v)
			},
			// People
			{
				id: "people",
				title: $i18n.t("people"),
				description: $i18n.t("people_dashboard_description"),
				iconify: "mdi:account-group",
				to: $routeUrl.PanelPeopleDashboardUrl(),
				company: null,
				items: [
					{
						id: "manage-users",
						title: $i18n.t("employees"),
						description: $i18n.t("manage_employees_description"),
						icon: "users",
						to: $routeUrl.PanelUsersUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_user_show || userStore.user?.allow_full_access ? "company-user" : null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-organization-units",
						title: $i18n.t("organization_units"),
						description: $i18n.t("manage_organization_units_description"),
						icon: "users",
						to: $routeUrl.PanelOrganizationUnitsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							(userStore.user?.allow_category_show &&
								!userStore.user?.allow_category_show_types?.length) ||
							userStore.user?.allow_category_show_types?.includes("organization-unit") ||
							userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-organization-chart",
						title: $i18n.t("organization_chart"),
						description: $i18n.t("organization_chart_description"),
						iconify: "mdi:family-tree",
						to: $routeUrl.PanelOrganizationChartUrl(),
						roles: ["admin", "support", "blogger", "developer", "user", "company-user"].filter((v) => v),
						company: true
					},
					{
						id: "manage-disciplinary-notes",
						title: $i18n.t("violations"),
						description: $i18n.t("disciplinary_notes_description"),
						iconify: "material-symbols:clinical-notes-rounded",
						to: $routeUrl.PanelDisciplinaryNotesUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_disciplinary_note_show ||
							userStore.user?.allow_disciplinary_note_show_types?.length ||
							userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					}
				]
			},
			// Recruitment
			{
				id: "recruitment",
				title: $i18n.t("recruitment"),
				description: $i18n.t("recruitment_dashboard_description"),
				iconify: "mdi:briefcase-account",
				to: $routeUrl.PanelRecruitmentDashboardUrl(),
				company: null,
				items: [
					{
						id: "manage-career-categories",
						title: $i18n.t("career_categories"),
						description: $i18n.t("manage_career_categories_description"),
						iconify: "mdi:shape",
						to: $routeUrl.PanelCareerCategoriesUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							(userStore.user?.allow_category_show &&
								!userStore.user?.allow_category_show_types?.length) ||
							userStore.user?.allow_category_show_types?.includes("career") ||
							userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-careers",
						title: $i18n.t("careers"),
						description: $i18n.t("manage_jobs_description"),
						icon: "work",
						to: $routeUrl.PanelCareersUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_career_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-career-requests",
						title: $i18n.t("job_applications"),
						description: $i18n.t("manage_applications_description"),
						icon: "work",
						to: $routeUrl.PanelCareerRequestsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_career_request_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-onboarding",
						title: $i18n.t("onboarding"),
						description: $i18n.t("manage_onboarding_description"),
						iconify: "mdi:account-check",
						to: $routeUrl.PanelOnboardingUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_career_request_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					}
				]
			},
			// Time & Attendance
			{
				id: "time-attendance",
				title: $i18n.t("time_and_attendance"),
				description: $i18n.t("time_attendance_dashboard_description"),
				iconify: "mdi:clock-check",
				to: $routeUrl.PanelTimeAttendanceDashboardUrl(),
				company: null,
				items: [
					{
						id: "manage-attendances",
						title: $i18n.t("attendance"),
						description: $i18n.t("manage_attendance_description"),
						iconify: "mdi:face-recognition",
						to: $routeUrl.PanelAttendancesUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_attendance_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					}
				]
			},
			// Payroll
			{
				id: "payroll",
				title: $i18n.t("payroll_and_compensation"),
				description: $i18n.t("payroll_dashboard_description"),
				iconify: "mdi:cash-multiple",
				to: $routeUrl.PanelPayrollDashboardUrl(),
				company: null,
				items: [
					{
						id: "manage-payrolls",
						title: $i18n.t("payrolls"),
						description: $i18n.t("manage_payrolls_description"),
						icon: "money",
						to: $routeUrl.PanelPayrollsUrl("salary"),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_payroll_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-reward-payrolls",
						title: $i18n.t("payroll_rewards"),
						description: $i18n.t("manage_bonus_payrolls_description"),
						icon: "money",
						to: $routeUrl.PanelPayrollsUrl("reward"),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_payroll_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					}
				]
			},
			// Performance
			{
				id: "performance",
				title: $i18n.t("performance_and_engagement"),
				description: $i18n.t("performance_dashboard_description"),
				iconify: "mdi:chart-line",
				to: $routeUrl.PanelPerformanceDashboardUrl(),
				company: null,
				items: [
					{
						id: "manage-performance-reviews",
						title: $i18n.t("performance_reviews"),
						description: $i18n.t("manage_performance_reviews_description"),
						icon: "positive-review",
						to: $routeUrl.PanelPerformanceReviewsUrl("normal"),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_performance_review_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-surveys",
						title: $i18n.t("surveys"),
						description: $i18n.t("manage_surveys_description"),
						iconify: "mdi:clipboard-list",
						to: $routeUrl.PanelSurveysUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							companyStore.company?.is_survey_module_enable ? "user" : null,
							companyStore.company?.is_survey_module_enable
								? userStore.user?.allow_survey_show || userStore.user?.allow_full_access
									? "company-user"
									: null
								: null
						].filter((v) => v),
						company: true
					}
				]
			},
			// Communication
			{
				id: "communication",
				title: $i18n.t("communication"),
				description: $i18n.t("communication_dashboard_description"),
				iconify: "mdi:message-text",
				to: $routeUrl.PanelCommunicationDashboardUrl(),
				company: null,
				items: [
					{
						id: "chat",
						title: $i18n.t("chat"),
						description: $i18n.t("chat_description"),
						iconify: "mdi:message",
						to: $routeUrl.PanelChatUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_chat_show || userStore.user?.allow_full_access ? "company-user" : null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-mails",
						title: $i18n.t("mails"),
						description: $i18n.t("mails_description"),
						iconify: "mdi:email",
						to: $routeUrl.PanelMailsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_mail_show || userStore.user?.allow_full_access ? "company-user" : null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-announcements",
						title: $i18n.t("announcements"),
						description: $i18n.t("announcements_description"),
						iconify: "mdi:announcement",
						to: $routeUrl.PanelAnnouncementsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_announcement_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-contacts",
						title: $i18n.t("contacts"),
						description: $i18n.t("contacts_description"),
						iconify: "mdi:account-multiple",
						to: $routeUrl.PanelContactsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_contacts_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "crm",
						title: $i18n.t("crm"),
						description: $i18n.t("crm_description"),
						iconify: "mdi:handshake",
						to: $routeUrl.PanelCrmUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_crm_deals_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					}
				]
			},
			// Workspace
			{
				id: "workspace",
				title: $i18n.t("workspace"),
				description: $i18n.t("workspace_dashboard_description"),
				iconify: "mdi:office-building",
				to: $routeUrl.PanelWorkspaceDashboardUrl(),
				company: null,
				items: [
					{
						id: "manage-projects",
						title: userStore.user?.is_tester ? $i18n.t("work_boards") : $i18n.t("projects"),
						description: userStore.user?.is_tester
							? $i18n.t("manage_boards_description")
							: $i18n.t("manage_projects_description"),
						iconify: "mingcute:table-2-fill",
						to: $routeUrl.PanelProjectsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_project_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-kitchen",
						title: $i18n.t("kitchen"),
						description: $i18n.t("kitchen_description"),
						iconify: "mdi:chef-hat",
						to: $routeUrl.PanelKitchenUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							companyStore.company?.is_food_module_enable ? "user" : null,
							companyStore.company?.is_food_module_enable
								? userStore.user?.allow_food_show || userStore.user?.allow_full_access
									? "company-user"
									: null
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "vira",
						title: $i18n.t("smart_assistant"),
						description: $i18n.t("smart_assistant_description"),
						iconify: "mingcute:ai-fill",
						to: $routeUrl.PanelViraUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							companyStore.company?.is_vira_enable && metaStore.isViraEnable ? "user" : null,
							userStore.user?.allow_full_access &&
							companyStore.company?.is_vira_enable &&
							metaStore.isViraEnable
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-benefit-funds",
						title: $i18n.t("benefit_funds"),
						description: $i18n.t("benefit_funds_description"),
						iconify: "mdi:piggy-bank",
						to: $routeUrl.PanelBenefitFundsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							companyStore.company?.is_benefit_fund_module_enable ? "user" : null,
							companyStore.company?.is_benefit_fund_module_enable
								? userStore.user?.allow_full_access
									? "company-user"
									: null
								: null
						].filter((v) => v),
						company: true
					}
				]
			},
			// CMS
			{
				id: "cms",
				title: $i18n.t("content_management"),
				description: $i18n.t("cms_dashboard_description"),
				iconify: "mdi:newspaper-variant-multiple",
				to: $routeUrl.PanelCmsDashboardUrl(),
				company: null,
				items: [
					{
						id: "manage-cms",
						title: $i18n.t("post_types"),
						description: $i18n.t("post_types_description"),
						iconify: "mdi:newspaper-variant-multiple",
						to: $routeUrl.PanelCmsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_cms_post_types_manage || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-cms-forms",
						title: $i18n.t("form"),
						description: $i18n.t("manage_forms_description"),
						iconify: "mdi:form-select",
						to: $routeUrl.PanelCmsFormsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_cms_form_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					...(postStore.types
						.map((t) => ({ ...t, isCustom: false }))
						?.map((type) => ({
							id: "cms-" + type.value,
							title: $stringUtil.getLocalText(type.name3) || "",
							description: $i18n.t("manage_n", { n: $stringUtil.getLocalText(type.name3) }),
							iconify: type.icon,
							to: $routeUrl.PanelCmsUrl() + "?type=" + type.value,
							roles: [
								"admin",
								"support",
								"blogger",
								"developer",
								"user",
								userStore.user?.allow_full_access ||
								(userStore.user?.allow_cms_show &&
									(!userStore.user?.allow_cms_show_types?.length ||
										userStore.user?.allow_cms_show_types?.includes(String(type.value))))
									? "company-user"
									: ""
							].filter((v) => v),
							company: true,
							items: undefined
						})) || []),
					...(companyStore.company?.custom_post_types?.map?.((type) => ({
						id: "cms-" + type.slug,
						title: $stringUtil.getLocalText(type.name) || "",
						description: $i18n.t("manage_n", { n: $stringUtil.getLocalText(type.name) }),
						iconify: "mdi:file-document",
						to: $routeUrl.PanelCmsUrl() + "?type=" + type.slug,
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_full_access ||
							(userStore.user?.allow_cms_show &&
								(!userStore.user?.allow_cms_show_types?.length ||
									userStore.user?.allow_cms_show_types?.includes(String(type.slug))))
								? "company-user"
								: ""
						].filter((v) => v),
						company: true,
						items: undefined
					})) || [])
				]
			},
			// Administration
			{
				id: "administration",
				title: $i18n.t("administration"),
				description: $i18n.t("administration_dashboard_description"),
				iconify: "mdi:cog",
				to: $routeUrl.PanelAdministrationDashboardUrl(),
				company: null,
				items: [
					{
						id: "manage-custom-pages",
						title: $i18n.t("custom_pages"),
						description: $i18n.t("manage_custom_pages_description"),
						iconify: "ooui:special-pages-rtl",
						to: $routeUrl.PanelCustomPagesUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_custom_page_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-settings",
						title: $i18n.t("settings"),
						description: $i18n.t("manage_settings_description"),
						iconify: "mdi:settings",
						to: $routeUrl.PanelSettingsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.role_id ? "company-user" : null
						].filter((v) => v),
						company: false
					},
					{
						id: "manage-company-settings",
						title: $i18n.t("company_settings"),
						description: $i18n.t("manage_company_settings_description"),
						iconify: "mdi:office-building-cog",
						to: $routeUrl.PanelCompanySettingsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_full_access ? "company-user" : null
						].filter((v) => v),
						company: true
					},
					{
						id: "manage-dev-tickets",
						title: $i18n.t("technical_tickets"),
						description: $i18n.t("manage_dev_tickets_description"),
						iconify: "mdi:lightbulb-question",
						to: $routeUrl.PanelDevTicketsUrl(),
						roles: [
							"admin",
							"support",
							"blogger",
							"developer",
							"user",
							userStore.user?.allow_dev_ticket_show || userStore.user?.allow_full_access
								? "company-user"
								: null
						].filter((v) => v),
						company: true
					}
				]
			},
			...(companyStore.company?.custom_pages?.map((customPage) => ({
				id: "custom-page-" + customPage.id,
				title: $stringUtil.getLocalText(customPage.title) || "",
				iconify: "simple-icons:codemagic",
				to: $routeUrl.PanelCustomPageUrl(customPage.id),
				roles: [
					"admin",
					"support",
					"blogger",
					"developer",
					"user",
					userStore.user?.allow_full_access ||
					(userStore.user?.allow_custom_page_show &&
						(!userStore.user?.allow_custom_page_show_ids?.length ||
							userStore.user?.allow_custom_page_show_ids?.includes(String(customPage.id))))
						? "company-user"
						: ""
				].filter((v) => v),
				company: true,
				items: undefined
			})) || [])
		]
	})

	const companyPanelItems = computed<CompanyPanelMenuItem[]>(() => {
		return [
			// Overview
			{
				id: "dashboard",
				title: $i18n.t("dashboard"),
				iconify: "mdi:view-dashboard",
				to: $routeUrl.CompanyDashboardUrl()
			},
			// Workspace
			{
				id: "workspace",
				title: $i18n.t("workspace"),
				description: $i18n.t("staff_workspace_description"),
				iconify: "mdi:office-building",
				to: $routeUrl.CompanyWorkspaceDashboardUrl(),
				items: [
					companyStore.company?.has_project_access && userStore.user?.is_tester
						? {
								id: "workspace-overview",
								title: $i18n.t("workspace_overview"),
								description: $i18n.t("workspace_overview_description"),
								iconify: "mdi:chart-box-outline",
								to: $routeUrl.CompanyWorkspaceOverviewUrl()
							}
						: (null as any),
					companyStore.company?.has_project_access
						? {
								id: "manage-projects",
								title: userStore.user?.is_tester ? $i18n.t("work_boards") : $i18n.t("projects"),
								description: userStore.user?.is_tester
									? $i18n.t("staff_boards_description")
									: $i18n.t("staff_projects_description"),
								iconify: "mingcute:table-2-fill",
								to: $routeUrl.CompanyProjectsUrl()
							}
						: (null as any),
					companyStore.company?.has_project_access && userStore.user?.is_tester
						? {
								id: "manage-special-projects",
								title: $i18n.t("projects"),
								description: $i18n.t("staff_projects_description"),
								iconify: "mdi:briefcase",
								to: $routeUrl.CompanyProjectsUrl() + "?type=special"
							}
						: (null as any),
					companyStore.company?.has_project_access
						? {
								id: "manage-tasks",
								title: $i18n.t("tasks"),
								description: $i18n.t("staff_tasks_description"),
								iconify: "mingcute:black-board-2-fill",
								to: $routeUrl.CompanyTasksUrl()
							}
						: (null as any),
					{
						id: "inbox",
						title: $i18n.t("my_notebook"),
						description: $i18n.t("staff_notebook_description"),
						iconify: "ri:inbox-fill",
						to: $routeUrl.CompanyInboxUrl()
					},
					companyStore.company?.is_food_module_enable
						? {
								id: "reserve-food",
								title: $i18n.t("reserve_food"),
								description: $i18n.t("staff_food_description"),
								iconify: "mdi:food",
								to: $routeUrl.CompanyReserveFoodUrl()
							}
						: null,
					companyStore.company?.is_benefit_fund_module_enable
						? {
								id: "manage-benefit-funds",
								title: $i18n.t("benefit_funds"),
								description: $i18n.t("staff_benefit_funds_description"),
								iconify: "mdi:piggy-bank",
								to: $routeUrl.CompanyBenefitFundsUrl()
							}
						: null
				].filter((v) => v)
			},
			// Communication
			{
				id: "communication",
				title: $i18n.t("communication"),
				description: $i18n.t("staff_communication_description"),
				iconify: "mdi:message-text",
				to: $routeUrl.CompanyCommunicationDashboardUrl(),
				items: [
					{
						id: "chat",
						title: $i18n.t("chat"),
						description: $i18n.t("staff_chat_description"),
						iconify: "mdi:message",
						to: $routeUrl.CompanyChatUrl(),
						new: companyStore.company?.total_unseen_messages || 0
					},
					{
						id: "manage-mails",
						title: $i18n.t("mails"),
						description: $i18n.t("staff_mails_description"),
						iconify: "mdi:email",
						to: $routeUrl.CompanyMailsUrl(),
						new: companyStore.company?.total_unseen_mails || 0
					},
					{
						id: "manage-announcements",
						title: $i18n.t("announcements"),
						description: $i18n.t("staff_announcements_description"),
						iconify: "mdi:announcement",
						to: $routeUrl.CompanyAnnouncementsUrl(),
						new: companyStore.company?.total_unseen_announcements || 0
					}
				].filter((v) => v)
			},
			// Payroll
			{
				id: "payroll",
				title: $i18n.t("payroll_and_compensation"),
				description: $i18n.t("staff_payroll_description"),
				iconify: "mdi:cash-multiple",
				to: $routeUrl.CompanyPayrollDashboardUrl(),
				items: [
					{
						id: "manage-payrolls",
						title: $i18n.t("payrolls"),
						description: $i18n.t("staff_payrolls_description"),
						icon: "money",
						to: $routeUrl.CompanyPayrollsUrl("salary"),
						new: companyStore.company?.total_unseen_payrolls || 0
					},
					{
						id: "manage-reward-payrolls",
						title: $i18n.t("payroll_rewards"),
						description: $i18n.t("staff_reward_payrolls_description"),
						icon: "money",
						to: $routeUrl.CompanyPayrollsUrl("reward"),
						new: companyStore.company?.total_unseen_reward_payrolls || 0
					}
				].filter((v) => v)
			},
			// Performance
			{
				id: "performance",
				title: $i18n.t("performance_and_engagement"),
				description: $i18n.t("staff_performance_description"),
				iconify: "mdi:chart-line",
				to: $routeUrl.CompanyPerformanceDashboardUrl(),
				items: [
					{
						id: "manage-performance-reviews",
						title: $i18n.t("performance_reviews"),
						description: $i18n.t("staff_performance_reviews_description"),
						icon: "positive-review",
						to: $routeUrl.CompanyPerformanceReviewsUrl("normal"),
						new: companyStore.company?.total_unseen_performance_reviews || 0
					},
					{
						id: "manage-disciplinary-notes",
						title: $i18n.t("violations"),
						description: $i18n.t("staff_disciplinary_notes_description"),
						iconify: "material-symbols:clinical-notes-rounded",
						to: $routeUrl.CompanyDisciplinaryNotesUrl(),
						new: companyStore.company?.total_unseen_disciplinary_notes || 0
					},
					companyStore.company?.is_survey_module_enable
						? {
								id: "manage-surveys",
								title: $i18n.t("surveys"),
								description: $i18n.t("staff_surveys_description"),
								iconify: "mdi:poll",
								to: $routeUrl.CompanySurveysUrl()
							}
						: null
				].filter((v) => v)
			},
			// Time & Attendance
			{
				id: "time-attendance",
				title: $i18n.t("time_and_attendance"),
				description: $i18n.t("staff_time_attendance_description"),
				iconify: "mdi:clock-check",
				to: $routeUrl.CompanyTimeAttendanceDashboardUrl(),
				items: [
					{
						id: "manage-attendances",
						title: $i18n.t("attendances"),
						description: $i18n.t("staff_attendances_description"),
						iconify: "mdi:face-recognition",
						to: $routeUrl.CompanyAttendancesUrl()
					}
				].filter((v) => v)
			},
			// Administration
			{
				id: "administration",
				title: $i18n.t("settings"),
				description: $i18n.t("staff_administration_description"),
				iconify: "mdi:cog",
				to: $routeUrl.CompanyAdministrationDashboardUrl(),
				items: [
					{
						id: "manage-settings",
						title: $i18n.t("settings"),
						description: $i18n.t("staff_settings_description"),
						iconify: "mdi:settings",
						to: $routeUrl.CompanySettingsUrl()
					}
				].filter((v) => v)
			},
			...(companyStore.company?.custom_pages?.map((customPage) => ({
				id: "custom-page-" + customPage.id,
				title: $stringUtil.getLocalText(customPage.title) || "",
				iconify: "simple-icons:codemagic",
				to: $routeUrl.CompanyCustomPageUrl(customPage.id),
				color: "#455a64"
			})) || [])
		].filter((v) => v)
	})

	const generatePanelMenuItems = (userType?: string) => {
		return panelMenuItems.value
			.map((groupItem) => ({
				...groupItem,
				items: groupItem.items
					? groupItem.items
							.filter((item) => !item.roles || item.roles.includes(userType || ""))
							.filter((item) => item.company === null || item.company === !!companyStore.companyId)
					: undefined
			}))
			.filter((groupItem) => !groupItem.items || groupItem.items.length)
			.filter((groupItem) => !groupItem.roles || groupItem.roles.includes(userType || ""))
			.filter((groupItem) => groupItem.company === null || groupItem.company === !!companyStore.companyId)
	}

	const generateCompanyPanelMenuItems = () => {
		return companyPanelItems.value
			.map((groupItem) => ({
				...groupItem,
				items: groupItem.items ? groupItem.items : undefined,
				new: groupItem.new || groupItem.items?.some?.((v) => v.new)
			}))
			.filter((groupItem) => !groupItem.items || groupItem.items.length)
	}

	return {
		...state,
		panelMenuItems,
		companyPanelItems,
		generatePanelMenuItems,
		generateCompanyPanelMenuItems
	}
})
